// Browser Web Push registration. Mirrors src/lib/push.ts (the iOS
// APNs side) but for desktop browsers (Chrome, Edge, Firefox,
// Safari 16+). The server-side fan-out lives in
// supabase/functions/_shared/webpush.ts and is wired into
// send-notification alongside the existing email + APNs paths.
//
// Flow on first opt-in:
//   1. Register sw.js (one-time per origin)
//   2. Request notification permission (browser shows the system
//      prompt — declined permissions are durable, browsers remember)
//   3. subscribe() the service worker's PushManager to our VAPID
//      public key — the browser returns a PushSubscription with
//      the endpoint URL + encryption keys
//   4. Upsert that subscription into web_push_subscriptions
//
// Subsequent app loads can call ensureSubscribed() to refresh
// last_seen_at, which lets the server prune dead browsers later.

import { api } from "./api";
import { isNativePlatform } from "./platform";

// VAPID public key for ShiftManager. Generated once; pairs with
// the VAPID_PRIVATE_KEY stored as a Supabase secret. Rotating
// this constant invalidates every existing subscription, so don't
// unless you mean to.
const VAPID_PUBLIC_KEY =
  "BFvqIAYDISva2I1lSz_NVJlET2hxgLrH4QCQtzmVO05z_wqlnj7UPL0A9245pSM8tkYlp9HUDbcKd1bI7hjTvXY";

const SW_PATH = (import.meta.env.BASE_URL ?? "/") + "sw.js";

// Per-device localStorage flags that gate the auto-prompt on
// sign-in. Both are stored as the literal string "true" — absence
// means default / unset.
//   AUTO_PROMPTED_KEY: we've already shown the system prompt once
//     to this browser. Without this we'd re-prompt every refresh,
//     which trains browsers (especially Chrome) to start silently
//     auto-denying.
//   DECLINED_KEY: the user explicitly hit Disable in Settings
//     after granting (or hit "not now" on some future banner).
//     Suppresses the auto-prompt so we don't fight their choice.
const AUTO_PROMPTED_KEY = "shift_manager.web_push_auto_prompted";
const DECLINED_KEY = "shift_manager.web_push_declined";

function base64UrlToUint8(b64url: string): Uint8Array {
  const pad = b64url.length % 4 ? 4 - (b64url.length % 4) : 0;
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(pad);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Encode the PushSubscription's binary fields to base64url so we
// can store them as text in Postgres. They're already URL-safe
// base64 in modern browsers — keep that encoding through to the
// server which decodes via the same helper.
function arrayBufferToB64url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}

export type WebPushStatus =
  | "unsupported"   // Browser lacks Push or Notification API
  | "blocked"       // User denied permission (browser remembers)
  | "default"       // User hasn't been asked yet
  | "granted";      // User said yes, subscription should be live

export async function getWebPushStatus(): Promise<WebPushStatus> {
  if (isNativePlatform) return "unsupported"; // iOS uses APNs path
  if (typeof window === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator)) return "unsupported";
  if (!("PushManager" in window)) return "unsupported";
  if (!("Notification" in window)) return "unsupported";
  const perm = Notification.permission;
  if (perm === "denied") return "blocked";
  if (perm === "granted") return "granted";
  return "default";
}

// True if any of the disqualifying conditions above hold. Used by
// the Settings panel to decide whether to render the toggle at all.
export async function webPushAvailable(): Promise<boolean> {
  const s = await getWebPushStatus();
  return s !== "unsupported";
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  // sw.js lives at the build root (Vite copies public/sw.js there).
  // The scope defaults to the directory of the script, which is the
  // app root — exactly what we want.
  const reg = await navigator.serviceWorker.register(SW_PATH);
  // Make sure the worker is active before we ask the PushManager
  // for a subscription; some browsers reject subscribe() while the
  // worker is still installing.
  await navigator.serviceWorker.ready;
  return reg;
}

async function persist(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  const endpoint = sub.endpoint;
  const p256dh = arrayBufferToB64url(sub.getKey?.("p256dh") ?? null)
    || json.keys?.p256dh
    || "";
  const auth = arrayBufferToB64url(sub.getKey?.("auth") ?? null)
    || json.keys?.auth
    || "";
  await api.registerWebPushSubscription({
    endpoint,
    p256dh,
    auth,
    userAgent: navigator.userAgent,
  });
}

// Opt-in entry point. Returns the new status so the caller can
// reflect it in the UI.
export async function enableWebPush(): Promise<WebPushStatus> {
  // User is re-engaging — clear the declined flag so future
  // auto-prompts (e.g. on a new device install) can fire again.
  try { localStorage.removeItem(DECLINED_KEY); } catch { /* noop */ }
  if (isNativePlatform) return "unsupported";
  if (!(await webPushAvailable())) return "unsupported";

  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm === "denied") return "blocked";
  if (perm !== "granted") return "default";

  const reg = await registerServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8(VAPID_PUBLIC_KEY) as BufferSource,
    });
  }
  await persist(sub);
  return "granted";
}

// Opt-out entry point. Removes the subscription on the device AND
// tells the server so the row gets revoked. Sets the declined
// flag so the next-load auto-prompt respects the user's choice
// instead of fighting them.
export async function disableWebPush(): Promise<void> {
  try { localStorage.setItem(DECLINED_KEY, "true"); } catch { /* noop */ }
  if (isNativePlatform) return;
  if (!(await webPushAvailable())) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await api.unregisterWebPushSubscription(endpoint);
  } catch (e) {
    console.warn("disableWebPush failed:", e);
  }
}

// Idempotent refresh — call on app launch when the user has
// already granted permission. Re-persists last_seen_at so the
// server knows the browser is still alive.
export async function ensureSubscribed(): Promise<void> {
  if (isNativePlatform) return;
  if (!(await webPushAvailable())) return;
  if (Notification.permission !== "granted") return;
  try {
    const reg = await registerServiceWorker();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8(VAPID_PUBLIC_KEY) as BufferSource,
      });
    }
    await persist(sub);
  } catch (e) {
    console.warn("ensureSubscribed failed:", e);
  }
}

// On-sign-in auto-prompt. Triggers the browser's permission dialog
// the FIRST time the user signs in on a given browser, and refreshes
// the subscription on every subsequent sign-in. Short-circuits in
// the following cases:
//   - Capacitor / iOS: APNs path handles it
//   - Browser lacks Push API: nothing to ask for
//   - Permission already denied: browser remembers, no point asking
//   - User explicitly disabled in Settings: respect their choice
//   - Already auto-prompted once on this browser: don't pester (Chrome
//     starts auto-denying after a few ignored prompts)
//
// Safe to call on every sign-in — the flags ensure the system dialog
// only ever pops once per device unless the user re-engages from
// Settings.
export async function autoPromptIfNeeded(): Promise<void> {
  if (isNativePlatform) return;
  if (!(await webPushAvailable())) return;

  const perm = Notification.permission;
  if (perm === "granted") {
    // Already opted in — refresh subscription and bail.
    await ensureSubscribed();
    return;
  }
  if (perm === "denied") return;

  // perm === "default" from here on. Respect explicit Settings opt-outs.
  try {
    if (localStorage.getItem(DECLINED_KEY) === "true") return;
    if (localStorage.getItem(AUTO_PROMPTED_KEY) === "true") return;
    localStorage.setItem(AUTO_PROMPTED_KEY, "true");
  } catch { /* localStorage blocked → just prompt once and move on */ }

  // Trigger the system prompt + subscribe in one shot.
  try { await enableWebPush(); } catch (e) {
    console.warn("autoPromptIfNeeded failed:", e);
  }
}
